import { ApolloClient, HttpLink, InMemoryCache, Observable, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

// Same-origin by default: the shared multi-tenant web build ships with VITE_API_URL empty,
// so every customer domain talks to its own /graphql via nginx (no per-tenant rebuild). Dev
// sets an absolute VITE_API_URL to reach the API on another port.
const API_URL = import.meta.env.VITE_API_URL || '/graphql';

const REFRESH_MUTATION = `
  mutation Refresh {
    refresh {
      access_token
      expires_in
      user {
        id
        email
        full_name
        roles
        permissions
      }
    }
  }
`;

interface RefreshResult {
  access_token: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    full_name: string;
    roles: string[];
    permissions: string[];
  };
}

function translateGraphqlMessage(message: string): string {
  if (message === 'consulting_room_already_occupied') {
    return 'El consultorio ya está ocupado con otra cuenta activa.';
  }
  if (message === 'doctor_session_mismatch') {
    return 'Debes firmar con el mismo usuario que inició sesión en esta estación.';
  }
  return message || 'Intenta nuevamente.';
}

let refresh_in_flight: Promise<RefreshResult | null> | null = null;

async function callRefresh(): Promise<RefreshResult | null> {
  if (refresh_in_flight) return refresh_in_flight;

  refresh_in_flight = (async () => {
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        credentials: 'include',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: REFRESH_MUTATION }),
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { data?: { refresh?: RefreshResult }; errors?: unknown };
      if (json.errors || !json.data?.refresh) return null;
      const result = json.data.refresh;
      useAuthStore.getState().setSession(result.access_token, {
        id: result.user.id,
        email: result.user.email,
        full_name: result.user.full_name,
        roles: result.user.roles as never,
        permissions: result.user.permissions,
      });
      return result;
    } catch {
      return null;
    } finally {
      refresh_in_flight = null;
    }
  })();

  return refresh_in_flight;
}

const error_link = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const operation_kind = operation.query.definitions.find(
    (definition) => String(definition.kind) === 'OperationDefinition',
  );
  const operation_type =
    operation_kind &&
    String(operation_kind.kind) === 'OperationDefinition' &&
    'operation' in operation_kind
      ? String(operation_kind.operation)
      : 'query';
  const should_notify = operation.operationName !== 'Refresh';

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const code = (err.extensions?.code as string | undefined) ?? '';
      const message = err.message ?? '';
      if (message.includes('workstation_unknown')) {
        useWorkstationStore.getState().clearWorkstation();
      }
      const looks_unauth = code === 'UNAUTHENTICATED' || message.includes('access_invalid') || message.includes('missing_bearer');
      const is_refresh_op = operation.operationName === 'Refresh' || operation.operationName === 'Login';

      if (looks_unauth && !is_refresh_op) {
        return new Observable((subscriber) => {
          callRefresh()
            .then((result) => {
              if (!result) {
                useAuthStore.getState().clearSession();
                subscriber.error(err);
                return;
              }
              const old_headers = operation.getContext().headers as Record<string, string> | undefined;
              operation.setContext({
                headers: {
                  ...old_headers,
                  Authorization: `Bearer ${result.access_token}`,
                },
              });
              forward(operation).subscribe({
                next: (v) => subscriber.next(v),
                error: (e) => subscriber.error(e),
                complete: () => subscriber.complete(),
              });
            })
            .catch((e) => subscriber.error(e));
        });
      }
      if (should_notify && !looks_unauth) {
        const translated_message = translateGraphqlMessage(message);
        showToast({
          tone: 'error',
          title:
            operation_type === 'mutation'
              ? 'No se pudo guardar el cambio'
              : 'No se pudieron cargar los datos',
          body: translated_message,
        });
      }
      console.warn('[GraphQL]', message, err.path);
    }
  }
  if (networkError) {
    if (should_notify) {
      showToast({
        tone: 'error',
        title: 'Sin conexión con el servidor',
        body: 'Revisa tu conexión o intenta nuevamente.',
      });
    }
    console.warn('[GraphQL network]', networkError);
  }
  return undefined;
});

const http_link = new HttpLink({
  uri: API_URL,
  credentials: 'include',
  fetch: (input, init) => {
    const access_token = useAuthStore.getState().access_token;
    const workstation_id = useWorkstationStore.getState().workstation_id;
    const headers = new Headers(init?.headers);
    if (access_token) headers.set('Authorization', `Bearer ${access_token}`);
    if (workstation_id) headers.set('X-Workstation-Id', workstation_id);
    headers.set('X-Client', 'totem-web');
    return fetch(input, { ...init, headers });
  },
});

export const apolloClient = new ApolloClient({
  link: from([error_link, http_link]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
    query: { fetchPolicy: 'network-only' },
    mutate: { errorPolicy: 'all' },
  },
});

export async function bootstrapSession(): Promise<boolean> {
  const result = await callRefresh();
  return Boolean(result);
}
